// What the coach marks say (the machinery is public/tour.js): the office's first visit, and each game the first time
// it's played (你画我猜, 谁是卧底, 狼人杀, the board games). OtterBonk teaches in its own 新手教程 instead, a run on this
// device (public/otterbonk/tutorial.js): nothing over a real run.
// The game modules call autoTour('<id>') where their screen opens, and put a tourButton('<id>') (「玩法」) in their
// header to see it again; 设置 / 新手引导 replays the office's and can bring every tour back.
// Unversioned on purpose (like icons.js), imported by the same URL from app.js and the game modules.
import { defineTour, autoTour, startTour, hint, tourButton, tourSeen, tourStore, resetTours, touring, shown } from '/tour.js';
import { t } from '/i18n.js';
export { autoTour, startTour, hint, tourButton, tourSeen, tourStore, resetTours, touring };

const touch = () => matchMedia('(pointer: coarse)').matches;

// ---- the office, the first time on board. ctx: { camera, me, MAP } (app.js)
// Something over the map (a game's full screen, a hut's card, the run, dress-up, a meeting): the office's tour waits.
const overMap = () => ['.gfull', '.ob', '.globby', '#gmpanel', '#dress-panel', '#meet'].some(shown);
defineTour('office', {
  name: () => t('tour.office.name'), ready: () => !overMap(),
  steps: (c) => {
    const hut = (id) => c.MAP?.zones?.find((z) => z.id === id);
    const onScreen = (z) => { if (!z || !c.camera) return null; const S = c.camera.S(); return { x: c.camera.x + (z.x + z.w / 2) * S, y: c.camera.y + (z.y + z.h / 2) * S, w: z.w * S, h: z.h * S }; };
    // the hut nearest to me: the camera goes to look at it for that step, then back to me
    const near = () => { const m = c.me?.(); const hs = ['game', 'spy', 'bonk'].map(hut).filter(Boolean); return m ? hs.sort((a, b) => Math.hypot(a.x + a.w / 2 - m.x, a.y + a.h / 2 - m.y) - Math.hypot(b.x + b.w / 2 - m.x, b.y + b.h / 2 - m.y))[0] : hs[0]; };
    let shown = null;
    return [
      { title: () => t('tour.office.walk.title'), at: () => { const m = c.me?.(); if (!m || !c.camera) return null; const S = c.camera.S(); return { x: c.camera.x + ((m.drawX ?? m.x) + 0.5) * S, y: c.camera.y + ((m.drawY ?? m.y) + 0.3) * S, w: 1.6 * S, h: 2 * S }; },
        text: () => touch() ? t('tour.office.walk.touch') : t('tour.office.walk.mouse') },
      { title: () => t('tour.office.voice.title'), round: true, pad: 0, at: () => { const m = c.me?.(); if (!m || !c.camera) return null; const S = c.camera.S(), r = 5 * S; return { x: c.camera.x + ((m.drawX ?? m.x) + 0.5) * S, y: c.camera.y + ((m.drawY ?? m.y) + 0.8) * S, w: 2 * r, h: 2 * r }; },
        text: () => touch() ? t('tour.office.voice.touch') : t('tour.office.voice.mouse') },
      { title: () => t('tour.office.emote.title'), at: '#emote', place: 'top', text: () => t('tour.office.emote.body') },
      // on the office raft the games are a boat away (the 活动舱): the step shows its 码头 instead of a hut
      near() ? { title: () => t('tour.office.hut.title'), at: () => onScreen(shown), pad: 4,
        enter: () => { shown = near(); if (shown && c.camera) c.camera.pan(shown.x + shown.w / 2, shown.y + shown.h / 2); },
        leave: () => c.camera?.follow(),
        text: () => t('tour.office.hut.body') }
      : { title: () => t('tour.office.boat.title', { play: t('floors.name.play') }), at: () => onScreen(shown), pad: 4,
        enter: () => { shown = c.MAP?.portals?.find((q) => q.to === 'play') ?? null; if (shown && c.camera) c.camera.pan(shown.x + shown.w / 2, shown.y + shown.h / 2); },
        leave: () => c.camera?.follow(),
        text: () => t('tour.office.boat.body', { play: t('floors.name.play') }) },
      { title: () => t('tour.office.dress.title'), at: '#dress', place: 'top', text: () => t('tour.office.dress.body') },
    ];
  },
});

// ---- 你画我猜 (games.js, #gfull)
defineTour('pictionary', {
  name: () => t('tour.pictionary.name'), alive: () => shown('#gfull'),
  steps: [
    { at: '#gfull .gword', text: () => t('tour.pictionary.word') },
    { at: '#gboard', text: () => t('tour.pictionary.board') },
    { at: '#gfull .gtools', text: () => t('tour.pictionary.tools'), skip: () => !document.querySelector('#gfull .gtools')?.offsetParent },
    { at: '#gfull .ginput', place: 'top', text: () => t('tour.pictionary.guess') },
    { at: ['#gfull .gtimer', '#gfull .gscores'], text: () => t('tour.pictionary.timer') },
  ],
});
// ---- 谁是卧底 (games.js, #sfull)
defineTour('spy', {
  name: () => t('tour.spy.name'), alive: () => shown('#sfull'),
  steps: [
    { at: '#sfull .smyword', text: () => t('tour.spy.word') },
    { at: '#sfull .sphase', text: () => t('tour.spy.describe') },
    { at: '#sfull .ginput', place: 'top', text: () => t('tour.spy.type') },
    { at: '#sfull .sseats', text: () => t('tour.spy.vote') },
  ],
});
// ---- 狼人杀 (games-werewolf.js, #wfull)
defineTour('werewolf', {
  name: () => t('tour.werewolf.name'), alive: () => shown('#wfull'),
  steps: [
    { at: '#wfull .wrole', text: () => t('tour.werewolf.role') },
    { at: '#wfull .sphase', text: () => t('tour.werewolf.phase') },
    { at: '#wfull .sseats', text: () => t('tour.werewolf.seats') },
    { at: '#wfull .ginput', place: 'top', text: () => t('tour.werewolf.speak') },
  ],
});

// ---- 心灵捕手 (games-mind.js, #mfull)
defineTour('mind', {
  name: () => t('tour.mind.name'), alive: () => shown('#mfull'),
  steps: [
    { at: '#mfull .sphase', text: () => t('tour.mind.phase') },
    { at: '#mfull .mseats', text: () => t('tour.mind.seats') },
    { at: '#mfull .ginput', place: 'top', text: () => t('tour.mind.chat') },
  ],
});

// ---- the board games at the tables (gomoku.js, #gmpanel): one tour per game, the same pointers with its own rules
const BOARD = {
  gomoku: ['tour.board.gomoku.name', 'tour.board.gomoku.rule'],
  go: ['tour.board.go.name', 'tour.board.go.rule'],
  othello: ['tour.board.othello.name', 'tour.board.othello.rule'],
  xiangqi: ['tour.board.xiangqi.name', 'tour.board.xiangqi.rule'],
  chess: ['tour.board.chess.name', 'tour.board.chess.rule'],
};
for (const [kind, [name, rule]] of Object.entries(BOARD)) defineTour('board-' + kind, {
  name: () => t(name), alive: () => shown('#gmpanel') && shown('#gmpanel .gmbtns'), // seated at the table (the buttons are a player's)
  steps: [
    { at: '#gmboard', place: 'left', text: () => t(rule) },
    { at: ['#gmpanel .gmseat.turn', '#gmpanel .gmseats'], place: 'left', text: () => t('tour.board.turn') },
    { at: '#gmpanel .gmstatus', place: 'left', text: () => t('tour.board.status') },
    { at: '#gmpanel .gmbtns', place: 'left', text: () => kind === 'go' ? t('tour.board.buttonsGo') : t('tour.board.buttons') },
  ],
});

/** Gone (OtterBonk's hints over a real run). Kept as a no-op: games-otterbonk.js may still import it (the build drops
 *  it from the bundle if nothing does). */
export function watchOtterBonk() {}

// ---- the office: the tour on the first welcome, and 设置 / 新手引导. app.js: initOfficeTour({ camera, me, MAP,
// addSettingsSection, send }); tours.sync(m) on the office's "tours" message; tours.welcome() on "welcome".
export function initOfficeTour({ camera, me, MAP, addSettingsSection, send }) {
  const ctx = { camera, me, MAP };
  addSettingsSection({
    id: 'tours', title: () => t('tour.settings.title'), icon: 'bulb', order: 900,
    render(box) {
      const row = document.createElement('div'); row.style.cssText = 'display:flex;gap:8px;flex-wrap:wrap';
      const again = tourButton('office', { text: t('tour.settings.replayOffice'), ctx });
      const replay = again.onclick; // close 设置 first: the tour points at the map and the bar under it
      again.onclick = (e) => { if (shown('#settings')) document.getElementById('settingsBtn')?.click(); replay(e); };
      const reset = document.createElement('button'); reset.type = 'button'; reset.className = 'tourbtn'; reset.textContent = t('tour.settings.reset');
      reset.onclick = () => { resetTours(); reset.textContent = t('tour.settings.resetDone'); reset.disabled = true; };
      row.append(again, reset);
      const p = document.createElement('p'); p.style.cssText = 'margin:6px 0 0;font-size:12px;color:#666'; p.textContent = t('tour.settings.hint');
      box.append(row, p);
    },
  });
  return {
    welcome: () => autoTour('office', ctx, { delay: 1500 }),
    // then the office's tour again: a reset on another device may have just brought it back
    sync: (m) => { tourStore(m.seen, (seen, reset) => send({ t: 'tours', seen, ...(reset ? { reset: true } : {}) }), Number(m.reset) || 0); autoTour('office', ctx, { delay: 1500 }); },
  };
}
